import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageHeader from '../shell/PageHeader';
import { FilterBar, DataTable } from '../patterns';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { listProcurement, Procurement, ProcurementSource, XeroImportResult } from '../../services/procurementService';
import { isChemical, type ConsumableType } from '../../services/consumableService';
import XeroImportModal from './XeroImportModal';
import { formatMeasure } from '../../utils/formatMeasure';
import { formatDate } from '../../dateFormat';

const SOURCE_CHIPS: { id: ProcurementSource; labelKey: string }[] = [
  { id: 'manual', labelKey: 'consumables.procurement.filters.source.manual' },
  { id: 'xero',   labelKey: 'consumables.procurement.filters.source.xero' },
];

const TYPE_CHIPS: { id: ConsumableType; labelKey: string }[] = [
  { id: 'chemical',   labelKey: 'consumables.catalog.filters.type.chemical' },
  { id: 'ppe',        labelKey: 'consumables.catalog.filters.type.ppe' },
  { id: 'fertilizer', labelKey: 'consumables.catalog.filters.type.fertilizer' },
  { id: 'diesel',     labelKey: 'consumables.catalog.filters.type.diesel' },
];

const ConsumableProcurementListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Procurement[]>([]);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState<ProcurementSource | null>(null);
  const [type, setType] = useState<ConsumableType | null>(null);
  const [xeroOpen, setXeroOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('consumables.title'), to: '/consumables/catalog' },
    { label: t('consumables.procurement.title') },
  ]);

  useEffect(() => {
    const params: Parameters<typeof listProcurement>[0] = {};
    if (search.trim()) params.supplier = search.trim();
    if (source) params.source = source;
    if (type) params.consumableType = type;
    listProcurement(params).then(r => setRows(r.rows)).catch(() => setRows([]));
  }, [search, source, type, refreshKey]);

  const handleXeroImported = (_result: XeroImportResult) => {
    setXeroOpen(false);
    setRefreshKey(k => k + 1);
  };

  const chips = [
    ...SOURCE_CHIPS.map(c => ({ id: `src:${c.id}`, label: t(c.labelKey), active: source === c.id })),
    ...TYPE_CHIPS.map(c => ({ id: `type:${c.id}`, label: t(c.labelKey), active: type === c.id })),
  ];

  const handleChipClick = (id: string) => {
    if (id.startsWith('src:')) {
      const v = id.slice(4) as ProcurementSource;
      setSource(prev => (prev === v ? null : v));
    } else if (id.startsWith('type:')) {
      const v = id.slice(5) as ConsumableType;
      setType(prev => (prev === v ? null : v));
    }
  };

  // Backend populate returns `null` only for true orphan rows (hard-deleted or
  // never-existing productId). Soft-deleted products populate normally with deletedAt set,
  // so ledger surfaces can render "<name> (deleted)" rather than losing the name.
  const deletedProductLabel = t('consumables.shared.deletedProduct', { defaultValue: '(deleted product)' });
  const deletedSuffix = t('consumables.shared.deletedSuffix', { defaultValue: '(deleted)' });

  const productDisplay = (r: Procurement) => {
    if (!r.productId) return deletedProductLabel;
    const lNumber = isChemical(r.productId) ? r.productId.lNumber : undefined;
    const base = `${r.productId.productName}${lNumber ? ` (${lNumber})` : ''}`;
    return r.productId.deletedAt ? `${base} ${deletedSuffix}` : base;
  };
  const productName = (r: Procurement) => {
    if (!r.productId) return deletedProductLabel;
    return r.productId.deletedAt
      ? `${r.productId.productName} ${deletedSuffix}`
      : r.productId.productName;
  };

  return (
    <div data-testid="consumables-procurement-list-page">
      <PageHeader
        title={t('consumables.procurement.title')}
        meta={t('consumables.procurement.meta')}
        actions={
          <>
            <button
              type="button"
              className="pat-btn pat-btn--subtle"
              onClick={() => setXeroOpen(true)}
            >
              {t('consumables.procurement.importFromXero')}
            </button>
            <button
              type="button"
              className="pat-btn pat-btn--primary"
              onClick={() => navigate('/consumables/procurement/new')}
            >
              {t('consumables.procurement.newProcurement')}
            </button>
          </>
        }
      />
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        chips={chips}
        onChipClick={handleChipClick}
      />
      <DataTable
        rows={rows}
        rowKey={(r) => r._id}
        onRowClick={(r) => navigate(`/consumables/procurement/${r._id}`)}
        columns={[
          {
            key: 'date',
            label: t('consumables.procurement.list.columns.date'),
            render: (r) => formatDate(r.date),
          },
          {
            key: 'supplier',
            label: t('consumables.procurement.list.columns.supplier'),
            sortable: true,
          },
          {
            key: 'product',
            label: t('consumables.procurement.list.columns.product'),
            render: productDisplay,
          },
          {
            key: 'quantity',
            label: t('consumables.procurement.list.columns.quantity'),
            align: 'right',
            render: (r) => formatMeasure(r.quantity, r.quantityUnit),
          },
          {
            key: 'unitPrice',
            label: t('consumables.procurement.list.columns.unitPrice'),
            align: 'right',
            render: (r) => r.unitPrice.toFixed(2),
          },
          {
            key: 'totalPrice',
            label: t('consumables.procurement.list.columns.totalPrice'),
            align: 'right',
            sortable: true,
            render: (r) => r.totalPrice.toFixed(2),
          },
          {
            key: 'source',
            label: t('consumables.procurement.list.columns.source'),
            render: (r) =>
              r.source === 'xero' ? (
                <>
                  <i className="bi bi-cloud-download" aria-hidden /> Xero
                </>
              ) : (
                'Manual'
              ),
          },
        ]}
        emptyState={t('consumables.procurement.list.empty')}
        mobileCardRow={(r) => (
          <div>
            <strong>{r.supplier}</strong>
            <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
              {formatDate(r.date)} · {productName(r)} · {formatMeasure(r.quantity, r.quantityUnit)} · {r.totalPrice.toFixed(2)}
            </div>
          </div>
        )}
      />
      <XeroImportModal
        open={xeroOpen}
        onCancel={() => setXeroOpen(false)}
        onImported={handleXeroImported}
      />
    </div>
  );
};

export default ConsumableProcurementListPage;
